// ─────────────────────────────────────────────────────────────────────────
// renderCostControlPdf(model)
//
// Renders a buildCostControlPdfModel() output into a print-ready HTML
// document (landscape A4). This function ONLY renders — it never
// calculates, never traverses React state, and has no knowledge
// whatsoever of whether the model came from Live or a Release data
// source. It only prints whatever header.dataSourceLabel/header.releaseInfo
// the model already carries as plain display strings.
//
// Given the same model, this function is deterministic — same model in,
// same HTML out, always.
// ─────────────────────────────────────────────────────────────────────────

const BRAND = {
  navy: '#0F2744', navy2: '#1A3A5C', blue: '#1B3A6B', blueLt: '#E8F0F9',
  teal: '#0D7A6E', tealLt: '#E6F5F3', red: '#C0392B', redLt: '#FDEDEC',
  amber: '#B7770D', amberLt: '#FEF9E7', green: '#1A7A4A', greenLt: '#E8F8EE',
  purple: '#7C3AED', purpleLt: '#F3EEFC',
  gray1: '#F4F7FA', gray2: '#E8EDF3', border: '#D8E0EA',
  text1: '#0F1E2D', text2: '#2C3E50', text3: '#5D7080', text4: '#8FA3B1',
}

const DEFAULT_LOGO_SVG = `<svg width="34" height="34" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
  <rect width="34" height="34" rx="7" fill="#0D7A6E"/>
  <path d="M9 22V12l8-4 8 4v10l-8 4-8-4Z" fill="none" stroke="white" stroke-width="1.6"/>
  <path d="M9 12l8 4 8-4M17 16v10" stroke="white" stroke-width="1.6" fill="none"/>
</svg>`

function fmt(n) {
  if (n == null || n === 0) return '<span class="dash">—</span>'
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n)
}
function fmtM(n) {
  const v = n || 0
  if (!v) return '0'
  const abs = Math.abs(v)
  if (abs >= 1000000) return (v / 1000000).toFixed(1) + 'M'
  if (abs >= 1000) return (v / 1000).toFixed(0) + 'K'
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(v)
}
function fmtSigned(n) {
  const v = n || 0
  if (v === 0) return '<span class="dash">—</span>'
  return (v >= 0 ? '+' : '') + fmt(v)
}
function fmtPct(n) {
  return (n || 0).toFixed(1) + '%'
}
function varClass(n) {
  return (n || 0) >= 0 ? 'var-pos' : 'var-neg'
}
function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]))
}
function pctDone(actual, bac) {
  return bac > 0 ? Math.min(Math.round(actual / bac * 100), 100) : 0
}
function pctColor(p) {
  return p >= 80 ? BRAND.red : p >= 50 ? BRAND.amber : BRAND.green
}

// ── Cover Page ──────────────────────────────────────────────────────────
function renderCoverPage(model) {
  const { header, executiveSummary: s } = model
  const logo = header.logoUrl
    ? `<img src="${esc(header.logoUrl)}" alt="logo" style="width:38px;height:38px;object-fit:contain;border-radius:8px;background:#fff" />`
    : DEFAULT_LOGO_SVG

  const releaseRows = header.releaseInfo ? `
    <div class="cover-grid" style="margin-top:0">
      <div><div class="cg-label">Release</div><div class="cg-value">#${esc(header.releaseInfo.releaseNumber ?? '—')}</div></div>
      <div><div class="cg-label">Forecast Period</div><div class="cg-value">${esc(header.releaseInfo.forecastPeriodLabel || '—')}</div></div>
      <div><div class="cg-label">Direct Revision</div><div class="cg-value">${esc(header.releaseInfo.directRevisionLabel || '—')}</div></div>
      <div><div class="cg-label">Indirect Revision</div><div class="cg-value">${esc(header.releaseInfo.indirectRevisionLabel || '—')}</div></div>
    </div>` : ''

  // Client/Consultant — plain metadata rows, only rendered if the user
  // has filled them in via the Reports config (Release Management →
  // Reports). No fallback values fabricated here.
  const clientConsultantGrid = (header.clientName || header.consultantName) ? `
    <div class="cover-grid">
      ${header.clientName ? `<div><div class="cg-label">Client</div><div class="cg-value">${esc(header.clientName)}</div></div>` : ''}
      ${header.consultantName ? `<div><div class="cg-label">Consultant</div><div class="cg-value">${esc(header.consultantName)}</div></div>` : ''}
    </div>` : ''

  const metaLines = [
    header.orgName ? esc(header.orgName) : null,
    header.orgWebsite ? esc(header.orgWebsite) : null,
    header.showPrintDate !== false ? `Generated ${esc(header.generatedDate)}` : null,
    (header.showGeneratedBy !== false && header.generatedBy) ? `By ${esc(header.generatedBy)}` : null,
  ].filter(Boolean).join('<br>')

  return `
<div class="page cover">
  <div class="cover-top">
    <div class="cover-logo">
      <div class="cover-logo-box">${logo}</div>
      <div>
        <div class="cover-logo-name">CostSpace Pro</div>
        <div class="cover-logo-sub">COST MANAGEMENT PLATFORM</div>
      </div>
    </div>
    <div class="cover-meta">${metaLines}</div>
  </div>
  <div class="cover-accent"></div>
  <div class="cover-body">
    <div class="cover-eyebrow">Monthly Financial Report</div>
    <div class="cover-report-type">${esc(header.dataSourceLabel)}</div>
    <div class="cover-title">${esc(header.reportTitle)}</div>
    <div class="cover-sub">${esc(header.projectName)} · ${esc(header.reportDate)}</div>
    <div class="cover-divider"></div>
    <div class="cover-grid">
      <div><div class="cg-label">Project</div><div class="cg-value">${esc(header.projectName)}</div></div>
      ${header.orgName ? `<div><div class="cg-label">Organization</div><div class="cg-value">${esc(header.orgName)}</div></div>` : ''}
      <div><div class="cg-label">Reporting Period</div><div class="cg-value">${esc(header.reportDate)}</div></div>
    </div>
    ${clientConsultantGrid}
    ${releaseRows}
    <div class="cover-kpis">
      <div class="ck-box"><div class="ck-label">Budget (BAC)</div><div class="ck-value">${fmtM(s.bac)}</div><div class="ck-sub">SAR</div></div>
      <div class="ck-box"><div class="ck-label">Total Actual Cost</div><div class="ck-value">${fmtM(s.actual)}</div><div class="ck-sub">SAR · To Date</div></div>
      <div class="ck-box"><div class="ck-label">EAC</div><div class="ck-value" style="color:${s.eac > s.bac ? '#FCA5A5' : '#6EE7B7'}">${fmtM(s.eac)}</div><div class="ck-sub">SAR · Forecast</div></div>
      <div class="ck-box"><div class="ck-label">Variance</div><div class="ck-value" style="color:${s.varianceCompletion >= 0 ? '#6EE7B7' : '#FCA5A5'}">${fmtSigned(s.varianceCompletion)}</div><div class="ck-sub">SAR · at Completion</div></div>
    </div>
  </div>
  <div class="cover-bottom">
    <div>${esc(header.footerText) || 'CostSpace Pro · Cost Management ERP'}</div>
    <div>🔒 ${esc(header.confidentialLabel) || 'Strictly Confidential — Top Management Only'}</div>
  </div>
</div>`
}

// ── Page chrome (header/footer wrapper for interior pages) ─────────────
function pageChrome(model, pageTitle, pageIcon, bodyHtml, pageLabel) {
  const { header } = model
  return `
<div class="page">
  <div class="page-header">
    <div class="ph-left">
      <div class="ph-icon">${pageIcon}</div>
      <div>
        <div class="ph-proj">${esc(pageTitle)}</div>
        <div class="ph-sub">${esc(header.projectName)} · ${esc(header.orgName)} · ${esc(header.reportDate)}</div>
      </div>
    </div>
    <div class="ph-right">${esc(header.dataSourceLabel)}<br>${esc(pageLabel || '')}</div>
  </div>
  <div class="page-body">
    ${bodyHtml}
  </div>
  <div class="page-footer">
    <div>${esc(header.footerText) || esc(header.reportTitle)} · ${esc(header.projectName)} · ${esc(header.reportDate)}</div>
    <div>🔒 ${esc(header.confidentialLabel) || 'Strictly Confidential — Top Management Only'}</div>
    <div>${esc(pageLabel || '')}</div>
  </div>
</div>`
}

// ── Executive Summary page ──────────────────────────────────────────────
function renderExecutiveSummary(s, model) {
  const cards = [
    ['Budget (BAC)', fmt(s.bac), null],
    ['Commitment', fmt(s.commitment), null],
    ['Accrual', fmt(s.accrual), null],
    ['Total Actual Cost', fmt(s.actual), 'bold'],
    ['ETC', fmt(s.etc), null],
    ['EAC', fmt(s.eac), 'bold'],
    ['Variance @ Completion', fmtSigned(s.varianceCompletion), s.varianceCompletion >= 0 ? 'pos' : 'neg'],
    // CPI — same threshold convention as Executive Report's own CPI card
    // (color: d.cpiVal>=1 ? green : amber) — 1.0 is on-budget/breakeven.
    ['CPI', (s.cpi || 0).toFixed(3), s.cpi >= 1 ? 'pos' : 'warn'],
  ]
  if (s.contractValue > 0) {
    cards.push(
      ['Contract Value', fmt(s.contractValue), null],
      ['Gross Profit', fmt(s.grossProfit), s.grossProfit >= 0 ? 'pos' : 'neg'],
      ['GP %', fmtPct(s.grossProfitPct), s.grossProfit >= 0 ? 'pos' : 'neg'],
    )
  }

  const body = `
    <div class="sec-hdr">
      <div class="sec-icon">📊</div>
      <div class="sec-title">Executive Summary</div>
      <div class="sec-sub">Project-wide financial position</div>
    </div>
    <div class="exec-summary">
      ${cards.map(([label, value, cls]) => `
        <div class="exec-card ${cls === 'bold' ? 'exec-card-strong' : ''}">
          <div class="exec-label">${label}</div>
          <div class="exec-value ${cls === 'pos' ? 'var-pos' : cls === 'neg' ? 'var-neg' : cls === 'warn' ? 'var-warn' : ''}">${value}</div>
        </div>`).join('')}
    </div>
    <div class="toc">
      <div class="sec-hdr" style="margin-top:20px">
        <div class="sec-icon">📑</div>
        <div class="sec-title">Contents</div>
      </div>
      <div class="toc-list">
        ${model.sections.map((sec, i) => `<div class="toc-item"><span class="toc-num">${String(i + 1).padStart(2, '0')}</span>${esc(sec.label)}</div>`).join('')}
      </div>
    </div>`

  return pageChrome(model, 'Executive Summary', '📊', body, 'Overview')
}

// ── Detail table rows ───────────────────────────────────────────────────
function renderRow(r, vc) {
  const indentClass = `indent${r.indent}`
  const levelClass =
    r.level === 'resource' ? 'row-resource' :
    r.level === 'costcode' ? 'row-costcode' :
    r.level === 'summaryrow' ? 'row-summaryrow' :
    r.level === 'summarytotal' ? 'row-summarytotal' :
    'row-subcode'

  const showCommitment = vc ? vc.commitment !== false : true
  const showAccrual = vc ? vc.accrual !== false : true
  const showEv = vc ? vc.ev === true : false
  const showCpi = vc ? vc.cpi === true : false

  if (r.level === 'summarytotal') {
    return `
    <tr class="${levelClass}">
      <td class="td-code" colspan="2">${esc(r.description)}</td>
      <td class="td-num">${fmt(r.bac)}</td>
      ${showCommitment ? `<td class="td-num">${fmt(r.commitment)}</td>` : ''}
      ${showAccrual ? `<td class="td-num">${r.accrual > 0 ? fmt(r.accrual) : '—'}</td>` : ''}
      <td class="td-num td-bold">${fmt(r.totalActualCost)}</td>
      <td class="td-num">${fmt(r.actualThisPeriod)}</td>
      <td class="td-num">${fmt(r.eac)}</td>
      <td class="td-num ${varClass(r.varianceCompletion)}">${fmtSigned(r.varianceCompletion)}</td>
      ${showEv ? `<td class="td-num">${fmt(r.earnedValue)}</td><td class="td-num ${varClass(r.costVariance)}">${fmtSigned(r.costVariance)}</td>` : ''}
      ${showCpi ? `<td class="td-num">${(r.cpi || 0).toFixed(2)}</td>` : ''}
    </tr>`
  }

  const p = pctDone(r.totalActualCost, r.bac)
  const pc = pctColor(p)
  const showProgress = r.level === 'costcode' || r.level === 'resource'

  return `
  <tr class="${indentClass} ${levelClass}">
    <td class="td-code">${esc(r.code)}</td>
    <td class="td-desc">${esc(r.description || '')}</td>
    <td class="td-num">${fmt(r.bac)}</td>
    ${showCommitment ? `<td class="td-num">${fmt(r.commitment)}</td>` : ''}
    ${showAccrual ? `<td class="td-num">${r.accrual > 0 ? fmt(r.accrual) : '—'}</td>` : ''}
    <td class="td-num td-bold">${fmt(r.totalActualCost)}</td>
    <td class="td-num">${fmt(r.actualThisPeriod)}</td>
    <td class="td-num">${fmt(r.eac)}</td>
    <td class="td-num ${varClass(r.varianceCompletion)}">${fmtSigned(r.varianceCompletion)}</td>
    ${showEv ? `<td class="td-num">${fmt(r.earnedValue)}</td><td class="td-num ${varClass(r.costVariance)}">${fmtSigned(r.costVariance)}</td>` : ''}
    ${showCpi ? `<td class="td-num">${(r.cpi || 0).toFixed(2)}</td>` : ''}
    <td class="td-prog">${showProgress ? `
      <div class="prog"><div class="prog-bar"><div class="prog-fill" style="width:${p}%;background:${pc}"></div></div><div class="prog-txt" style="color:${pc}">${p}%</div></div>
    ` : ''}</td>
  </tr>`
}

const SECTION_ICONS = {
  material: '📦', labour: '👷', subcontract: '🤝', equipment: '🚜',
  indirect: '🏢', summary: '📋', direct: '🏗️',
}

function sectionIcon(section) {
  return SECTION_ICONS[section.key] || SECTION_ICONS[section.icon] || '📁'
}

function sectionKpiStrip(section) {
  const t = section.totals
  return `
    <div class="kpi-strip">
      <div class="kb navy"><div class="kb-label">Budget</div><div class="kb-value">${fmtM(t.bac)}</div></div>
      <div class="kb blue"><div class="kb-label">Commitment</div><div class="kb-value">${fmtM(t.commitment)}</div></div>
      <div class="kb teal"><div class="kb-label">Actual Cost</div><div class="kb-value">${fmtM(t.totalActualCost)}</div></div>
      <div class="kb amber"><div class="kb-label">This Period</div><div class="kb-value">${fmtM(t.actualThisPeriod)}</div></div>
      <div class="kb ${t.varianceCompletion >= 0 ? 'green' : 'red'}">
        <div class="kb-label">Variance</div><div class="kb-value">${fmtM(t.varianceCompletion)}</div>
      </div>
    </div>`
}

function renderDetailTable(section, visibleCols) {
  const isSummary = section.isSummary
  const showCommitment = visibleCols ? visibleCols.commitment !== false : true
  const showAccrual = visibleCols ? visibleCols.accrual !== false : true
  const showEv = visibleCols ? visibleCols.ev === true : false
  const showCpi = visibleCols ? visibleCols.cpi === true : false

  const commitmentTh = showCommitment ? '<th>Commitment</th>' : ''
  const accrualTh = showAccrual ? '<th>Accrual</th>' : ''
  const evTh = showEv ? '<th>Earned Value</th><th>Cost Variance</th>' : ''
  const cpiTh = showCpi ? '<th>CPI</th>' : ''
  const cols = isSummary
    ? `<th>Category</th><th></th><th>Budget (BAC)</th>${commitmentTh}${accrualTh}<th>Total Actual</th><th>This Period</th><th>EAC</th><th>Variance</th>${evTh}${cpiTh}`
    : `<th>Code · Description</th><th></th><th>Budget (BAC)</th>${commitmentTh}${accrualTh}<th>Total Actual</th><th>This Period</th><th>EAC</th><th>Var @ Comp</th>${evTh}${cpiTh}<th>% Done</th>`

  return `
    <div class="detail-tbl-wrap">
      <table>
        <thead><tr>${cols}</tr></thead>
        <tbody>
          ${section.rows.map(r => renderRow(r, visibleCols)).join('')}
        </tbody>
        <tfoot>
          <tr class="grand-total">
            <td colspan="2">TOTAL — ${esc(section.label)}</td>
            <td>${fmt(section.totals.bac)}</td>
            ${showCommitment ? `<td>${fmt(section.totals.commitment)}</td>` : ''}
            ${showAccrual ? `<td>${section.totals.accrual > 0 ? fmt(section.totals.accrual) : '—'}</td>` : ''}
            <td>${fmt(section.totals.totalActualCost)}</td>
            <td>${fmt(section.totals.actualThisPeriod)}</td>
            <td>${fmt(section.totals.eac)}</td>
            <td class="${varClass(section.totals.varianceCompletion)}">${fmtSigned(section.totals.varianceCompletion)}</td>
            ${showEv ? `<td>${fmt(section.totals.earnedValue)}</td><td class="${varClass(section.totals.costVariance)}">${fmtSigned(section.totals.costVariance)}</td>` : ''}
            ${showCpi ? `<td>${(section.totals.cpi || 0).toFixed(2)}</td>` : ''}
            ${isSummary ? '' : '<td></td>'}
          </tr>
        </tfoot>
      </table>
    </div>`
}

function renderSectionPage(model, section, index, total) {
  const body = `
    ${section.isSummary ? '' : sectionKpiStrip(section)}
    <div class="sec-hdr">
      <div class="sec-icon">${sectionIcon(section)}</div>
      <div class="sec-title">${esc(section.label)}${section.isSummary ? '' : ' — Cost Breakdown'}</div>
      <div class="sec-sub">SAR · ${esc(model.header.reportDate)}</div>
    </div>
    ${renderDetailTable(section, model.visibleCols)}
    ${section.isSummary ? `
    <div class="notes-box">
      <strong>Notes:</strong> Total Actual Cost = Invoiced + Accruals · EAC defaults to Budget (BAC) when no forecast entry exists ·
      All amounts in SAR · Report data source: ${esc(model.header.dataSourceLabel)} · Extracted from CostSpace Pro as of ${esc(model.header.generatedDate)}
    </div>` : ''}
  `
  return pageChrome(model, section.label, sectionIcon(section), body, `Page ${index + 2} of ${total + 1}`)
}

// ── Styles ───────────────────────────────────────────────────────────────
const STYLES = `
  :root {
    --navy:${BRAND.navy}; --navy2:${BRAND.navy2}; --blue:${BRAND.blue}; --blue-lt:${BRAND.blueLt};
    --teal:${BRAND.teal}; --teal-lt:${BRAND.tealLt}; --red:${BRAND.red}; --red-lt:${BRAND.redLt};
    --amber:${BRAND.amber}; --amber-lt:${BRAND.amberLt}; --green:${BRAND.green}; --green-lt:${BRAND.greenLt};
    --purple:${BRAND.purple}; --purple-lt:${BRAND.purpleLt};
    --gray1:${BRAND.gray1}; --gray2:${BRAND.gray2}; --border:${BRAND.border};
    --text1:${BRAND.text1}; --text2:${BRAND.text2}; --text3:${BRAND.text3}; --text4:${BRAND.text4}; --white:#FFFFFF;
  }
  @page { size: A4 landscape; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin:0; padding:0; }
  body { font-family:'Plus Jakarta Sans','Segoe UI',Arial,sans-serif; background:#CBD5E0; color:var(--text1); font-size:12.5px; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  .page { width:297mm; min-height:210mm; background:var(--white); margin:0 auto; box-shadow:0 8px 40px rgba(0,0,0,.15); position:relative; overflow:hidden; page-break-after:always; display:flex; flex-direction:column; }

  /* ── COVER ────────────────────────────────────────────────────────── */
  .cover { background:var(--navy); }
  .cover-top { padding:26px 40px 18px; display:flex; justify-content:space-between; align-items:flex-start; border-bottom:1px solid rgba(255,255,255,.08); }
  .cover-logo { display:flex; align-items:center; gap:10px; }
  .cover-logo-box { width:38px; height:38px; background:var(--teal); border-radius:8px; display:flex; align-items:center; justify-content:center; overflow:hidden; }
  .cover-logo-name { font-size:14px; font-weight:800; color:white; }
  .cover-logo-sub { font-size:8.5px; color:rgba(255,255,255,.4); letter-spacing:1.5px; }
  .cover-meta { text-align:right; color:rgba(255,255,255,.45); font-size:9px; line-height:1.9; }
  .cover-accent { height:4px; background:linear-gradient(90deg,var(--teal),var(--blue),var(--navy2)); }
  .cover-body { flex:1; padding:36px 40px 28px; display:flex; flex-direction:column; justify-content:center; }
  .cover-eyebrow { font-size:9px; letter-spacing:4px; text-transform:uppercase; color:rgba(255,255,255,.4); margin-bottom:6px; }
  .cover-report-type { font-size:10.5px; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:#5EEAD4; background:rgba(13,122,110,.2); border-left:3px solid #5EEAD4; display:inline-block; padding:5px 12px; border-radius:0 4px 4px 0; margin-bottom:18px; }
  .cover-title { font-size:26px; font-weight:800; color:white; line-height:1.2; margin-bottom:6px; }
  .cover-sub { font-size:14px; color:rgba(255,255,255,.5); font-weight:300; margin-bottom:26px; }
  .cover-divider { width:50px; height:3px; background:var(--teal); border-radius:2px; margin-bottom:24px; }
  .cover-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:18px; margin-bottom:22px; }
  .cg-label { font-size:8px; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,.35); margin-bottom:4px; }
  .cg-value { font-size:12.5px; font-weight:700; color:white; }
  .cover-kpis { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
  .ck-box { background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.1); border-radius:8px; padding:12px 14px; }
  .ck-label { font-size:8px; letter-spacing:1px; text-transform:uppercase; color:rgba(255,255,255,.4); margin-bottom:4px; }
  .ck-value { font-size:16px; font-weight:800; font-family:'JetBrains Mono',monospace; color:white; }
  .ck-sub { font-size:8.5px; color:rgba(255,255,255,.4); margin-top:2px; }
  .cover-bottom { padding:14px 40px; background:rgba(0,0,0,.2); display:flex; justify-content:space-between; align-items:center; font-size:8.5px; color:rgba(255,255,255,.35); }

  /* ── PAGE CHROME ──────────────────────────────────────────────────── */
  .page-header { background:var(--navy); padding:11px 30px; display:flex; justify-content:space-between; align-items:center; }
  .ph-left { display:flex; align-items:center; gap:10px; }
  .ph-icon { font-size:14px; }
  .ph-proj { font-size:11px; font-weight:700; color:white; }
  .ph-sub { font-size:9px; color:rgba(255,255,255,.5); }
  .ph-right { font-size:8.5px; color:rgba(255,255,255,.5); text-align:right; line-height:1.5; }
  .page-body { flex:1; padding:16px 30px 20px; overflow:visible; }
  .page-footer { padding:8px 30px; background:var(--gray1); border-top:1px solid var(--border); display:flex; justify-content:space-between; font-size:8px; color:var(--text4); }

  /* ── SECTION HEADER ───────────────────────────────────────────────── */
  .sec-hdr { display:flex; align-items:center; gap:8px; margin-bottom:10px; padding-bottom:6px; border-bottom:2px solid var(--gray2); }
  .sec-icon { font-size:14px; }
  .sec-title { font-size:12.5px; font-weight:800; color:var(--navy); flex:1; }
  .sec-sub { font-size:9px; color:var(--text4); }

  /* ── EXEC SUMMARY CARDS ───────────────────────────────────────────── */
  .exec-summary { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:6px; }
  .exec-card { border:1px solid var(--border); border-radius:8px; padding:10px 12px; background:var(--gray1); }
  .exec-card-strong { background:var(--blue-lt); border-color:var(--blue); }
  .exec-label { font-size:8px; color:var(--text3); text-transform:uppercase; letter-spacing:.05em; margin-bottom:3px; }
  .exec-value { font-size:15px; font-weight:800; font-family:'JetBrains Mono',monospace; color:var(--text1); }
  .toc-list { display:grid; grid-template-columns:repeat(2,1fr); gap:6px 20px; }
  .toc-item { font-size:11px; color:var(--text2); padding:5px 0; border-bottom:1px dotted var(--border); display:flex; gap:8px; }
  .toc-num { color:var(--teal); font-weight:800; font-family:'JetBrains Mono',monospace; font-size:10px; }

  /* ── KPI STRIP (per section) ──────────────────────────────────────── */
  .kpi-strip { display:grid; grid-template-columns:repeat(5,1fr); gap:10px; margin-bottom:14px; }
  .kb { border-radius:8px; padding:9px 12px; color:white; }
  .kb-label { font-size:7.5px; text-transform:uppercase; letter-spacing:.05em; opacity:.75; margin-bottom:3px; }
  .kb-value { font-size:14px; font-weight:800; font-family:'JetBrains Mono',monospace; }
  .kb.navy { background:var(--navy); }
  .kb.blue { background:var(--blue); }
  .kb.teal { background:var(--teal); }
  .kb.amber { background:var(--amber); }
  .kb.green { background:var(--green); }
  .kb.red { background:var(--red); }

  /* ── DETAIL TABLE ──────────────────────────────────────────────────── */
  .detail-tbl-wrap table { width:100%; border-collapse:collapse; font-size:9.5px; }
  thead tr { background:var(--navy); }
  thead th { color:white; text-transform:uppercase; letter-spacing:.04em; font-size:7.8px; font-weight:700; padding:6px 8px; text-align:right; }
  thead th:first-child { text-align:left; }
  tbody td, tfoot td { padding:4.5px 8px; border-bottom:1px solid var(--gray2); }
  .td-code { font-family:'JetBrains Mono',monospace; font-weight:700; color:var(--text2); white-space:nowrap; }
  .td-desc { color:var(--text2); }
  .td-num { text-align:right; font-variant-numeric:tabular-nums; font-family:'JetBrains Mono',monospace; color:var(--text2); }
  .td-bold { font-weight:700; color:var(--text1); }
  .dash { color:var(--text4); font-weight:400; }
  .td-prog { min-width:80px; }
  .row-costcode { background:var(--gray1); }
  .row-costcode .td-code, .row-costcode .td-desc { font-weight:800; color:var(--navy); }
  .row-resource td { background:var(--purple-lt); }
  .row-resource .td-code { color:var(--purple); }
  .row-summaryrow td { }
  .row-summarytotal td { background:var(--gray2); font-weight:700; border-top:1.5px solid var(--navy); border-bottom:1.5px solid var(--navy); }
  .indent1 .td-code { padding-left:20px; }
  .indent2 .td-code { padding-left:34px; }
  .var-pos { color:var(--green); font-weight:700; }
  .var-neg { color:var(--red); font-weight:700; }
  .var-warn { color:var(--amber); font-weight:700; }
  tfoot .grand-total td { background:var(--navy); color:white; font-weight:800; font-size:10px; border:none; }
  tfoot .grand-total .var-pos { color:#6EE7B7; }
  tfoot .grand-total .var-neg { color:#FCA5A5; }

  .prog { display:flex; align-items:center; gap:6px; }
  .prog-bar { flex:1; height:5px; background:var(--gray2); border-radius:3px; overflow:hidden; min-width:36px; }
  .prog-fill { height:100%; border-radius:3px; }
  .prog-txt { font-size:8px; font-weight:700; min-width:26px; }

  .notes-box { margin-top:12px; background:var(--blue-lt); border:1px solid var(--border); border-radius:6px; padding:9px 14px; font-size:8.5px; color:var(--text2); line-height:1.6; }

  @media print {
    body { background:white; padding:0; }
    .page { box-shadow:none; margin:0; }
  }
`

export function renderCostControlPdf(model) {
  const pages = []
  if (model.includeCoverPage !== false) pages.push(renderCoverPage(model))
  if (model.includeExecutiveSummary !== false) pages.push(renderExecutiveSummary(model.executiveSummary, model))
  model.sections.forEach((section, i) => {
    pages.push(renderSectionPage(model, section, i, model.sections.length))
  })

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${esc(model.header.reportTitle)} — ${esc(model.header.projectName)}</title>
<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>${STYLES}</style>
</head>
<body>
${pages.join('\n')}
</body>
</html>`
}
